import axios from "axios";
import { DataTable } from "simple-datatables";
import { showAlert, showConfirm, handleRequestError } from "../../utils/alert";
import VMasker from "vanilla-masker";

export default () => ({
  // Modal States
  isModalOpen: false,
  isSubmitting: false,

  // Edit States
  editMode: false,
  editId: null,

  // Data Storage
  productsList: [],

  // Form Payload (Stock removed)
  productForm: {
    name: "",
    description: "",
    price: "",
    is_active: true,
  },

  // DataTable Instance
  productsDataTable: null,

  async initProducts() {
    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }

    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    await this.loadProductsTable();
  },

  // ================= FORMATTING HELPERS =================

  formatPrice(price) {
    return parseFloat(price).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  },

  applyMoneyMask(value) {
    if (!value) return "";
    const onlyNumbers = value.toString().replace(/\D/g, "");
    return VMasker.toMoney(onlyNumbers, {
      precision: 2,
      separator: ",",
      delimiter: ".",
      unit: "R$ ",
      zeroCents: false,
    });
  },

  handlePriceInput(event) {
    this.productForm.price = this.applyMoneyMask(event.target.value);
  },

  unmaskMoney(maskedValue) {
    if (!maskedValue) return 0;
    const cleanString = maskedValue
      .toString()
      .replace("R$ ", "")
      .replace(/\./g, "")
      .replace(",", ".");
    return parseFloat(cleanString);
  },

  // ================= DATA TABLE =================

  async loadProductsTable() {
    try {
      const response = await axios.get("/api/client/products");
      this.productsList = response.data.data;

      if (this.productsDataTable) {
        this.productsDataTable.destroy();
      }

      const tbody = document.querySelector("#productsTable tbody");
      tbody.innerHTML = this.productsList
        .map((product) => {
          const statusBadge = product.is_active
            ? `<span class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-bold text-green-700 ring-1 ring-inset ring-green-600/20">Ativo</span>`
            : `<span class="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-bold text-gray-600 ring-1 ring-inset ring-gray-500/10">Inativo</span>`;

          return `
            <tr>
              <td class="py-4 px-4 xl:px-6">
                <p class="font-bold text-slate-800">${product.name}</p>
                ${product.description ? `<p class="text-xs text-slate-500 mt-0.5 truncate max-w-xs">${product.description}</p>` : ""}
              </td>
              <td class="py-4 px-4 xl:px-6 font-medium text-slate-700">
                ${this.formatPrice(product.price)}
              </td>
              <td class="py-4 px-4 xl:px-6">
                ${statusBadge}
              </td>
              <td class="text-left py-4 px-4 xl:px-6">
                <div class="flex justify-start gap-2">
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('edit-product', {detail: ${product.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    Editar
                  </button>
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('delete-product', {detail: ${product.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600"
                  >
                    Excluir
                  </button>
                </div>
              </td>
            </tr>
          `;
        })
        .join("");

      this.productsDataTable = new DataTable("#productsTable", {
        searchable: false,
        perPageSelect: false,
        sortable: false,
        labels: {
          info: "Mostrando de {start} até {end} de {rows} produtos",
          noRows: "Nenhum produto cadastrado",
          noResults: "Nenhum resultado encontrado",
          searchTitle: "Pesquisar produto...",
        },
      });
    } catch (error) {
      console.error(error);
    }
  },

  // ================= MODAL HANDLERS =================

  openCreateProduct() {
    this.editMode = false;
    this.productForm = {
      name: "",
      description: "",
      price: "",
      is_active: true,
    };
    this.isModalOpen = true;
  },

  openEditProduct(id) {
    this.editMode = true;
    this.editId = id;
    const product = this.productsList.find((p) => p.id === id);

    this.productForm = {
      name: product.name,
      description: product.description || "",
      price: this.applyMoneyMask(product.price),
      is_active: product.is_active,
    };
    this.isModalOpen = true;
  },

  // ================= SUBMIT =================

  async submitProduct() {
    this.isSubmitting = true;
    try {
      const apiPayload = {
        ...this.productForm,
        price: this.unmaskMoney(this.productForm.price),
      };

      if (this.editMode) {
        await axios.put(`/api/client/products/${this.editId}`, apiPayload);
        showAlert("Produto atualizado com sucesso!", "success");
      } else {
        await axios.post("/api/client/products", apiPayload);
        showAlert("Produto criado com sucesso!", "success");
      }

      await this.loadProductsTable();
      this.isModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao guardar o produto.");
    } finally {
      this.isSubmitting = false;
    }
  },

  // ================= DELETE =================

  async deleteProduct(id) {
    const result = await showConfirm(
      "Excluir Produto",
      "Tem certeza que deseja apagar este produto? Se ele já foi vendido, recomendamos apenas Desativar para manter o histórico financeiro intacto.",
    );

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/client/products/${id}`);
        await this.loadProductsTable();
        showAlert("Produto removido.", "success");
      } catch (error) {
        handleRequestError(error, "Erro ao excluir o produto.");
      }
    }
  },
});
